import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useWallets } from '../WalletContext';
import { useCategories } from '../hooks/useCategories';
import './AddExpenseModal.css';

function EditTransactionModal({ transaction, onClose, onSave }) {
  const { wallets } = useWallets();
  const { categories, loading: categoriesLoading } = useCategories();
  
  const isIncome = transaction.type === 'income';
  const isHouseholdExpense = transaction.type === 'household_expense';
  
  // Income form data
  const [incomeData, setIncomeData] = useState({
    wallet_id: transaction.wallet_id || '',
    date: transaction.date || new Date().toISOString().split('T')[0],
    source: transaction.source || '',
    description: transaction.description || '',
    gross_amount: transaction.gross_amount || transaction.amount || '',
    income_tax: transaction.income_tax || 0,
    social_contributions: transaction.social_contributions || 0,
    student_loan: transaction.student_loan || 0,
    currency: transaction.currency || 'GBP',
    notes: transaction.notes || ''
  });

  // Expense form data
  const [expenseData, setExpenseData] = useState({
    wallet_id: transaction.wallet_id || '',
    date: transaction.date || new Date().toISOString().split('T')[0],
    category: transaction.category || '',
    subcategory: transaction.subcategory || '',
    description: transaction.description || '',
    amount: transaction.amount || '',
    currency: transaction.currency || 'GBP',
    notes: transaction.notes || ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Calculate net amount for income
  const calculateNetAmount = () => {
    const gross = parseFloat(incomeData.gross_amount) || 0;
    const tax = parseFloat(incomeData.income_tax) || 0;
    const social = parseFloat(incomeData.social_contributions) || 0;
    const loan = parseFloat(incomeData.student_loan) || 0;
    return gross - tax - social - loan;
  };

  const getCurrencySymbol = (currency) => {
    const symbols = {
      'EUR': '€', 'USD': '$', 'GBP': '£', 'CHF': 'CHF',
      'JPY': '¥', 'CAD': 'C$', 'AUD': 'A$', 'CNY': '¥', 'INR': '₹'
    };
    return symbols[currency] || currency;
  };

  const handleIncomeChange = (field, value) => {
    setIncomeData(prev => ({ ...prev, [field]: value }));
  };

  const handleExpenseChange = (field, value) => {
    setExpenseData(prev => ({ ...prev, [field]: value }));
  };

  const handleCategoryChange = (category) => {
    const subcategories = categories[category]?.subcategories || [];
    setExpenseData(prev => ({
      ...prev,
      category,
      subcategory: subcategories[0] || ''
    }));
  };

  const handleWalletChange = (walletId, isIncomeForm) => {
    const wallet = wallets.find(w => w.id === parseInt(walletId));
    if (isIncomeForm) {
      setIncomeData(prev => ({
        ...prev,
        wallet_id: walletId,
        currency: wallet?.currency || 'GBP'
      }));
    } else {
      setExpenseData(prev => ({
        ...prev,
        wallet_id: walletId,
        currency: wallet?.currency || 'GBP'
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (isIncome) {
        const payload = {
          wallet_id: parseInt(incomeData.wallet_id),
          date: incomeData.date,
          source: incomeData.source,
          description: incomeData.description,
          gross_amount: parseFloat(incomeData.gross_amount),
          income_tax: parseFloat(incomeData.income_tax) || 0,
          social_contributions: parseFloat(incomeData.social_contributions) || 0,
          student_loan: parseFloat(incomeData.student_loan) || 0,
          net_amount: calculateNetAmount(),
          currency: incomeData.currency,
          notes: incomeData.notes
        };
        
        await api.put(`/personal/income/${transaction.id}`, payload);
      } else if (isHouseholdExpense) {
        // For household expenses, we update differently
        const payload = {
          date: expenseData.date,
          category: expenseData.category,
          subcategory: expenseData.subcategory,
          description: expenseData.description,
          amount: parseFloat(expenseData.amount),
          currency: expenseData.currency,
          notes: expenseData.notes
        };
        
        await api.put(`/household/${transaction.household_id}/expenses/${transaction.id}`, payload);
      } else {
        const payload = {
          wallet_id: parseInt(expenseData.wallet_id),
          date: expenseData.date,
          category: expenseData.category,
          subcategory: expenseData.subcategory,
          description: expenseData.description,
          amount: parseFloat(expenseData.amount),
          currency: expenseData.currency,
          notes: expenseData.notes
        };
        
        await api.put(`/personal/expenses/${transaction.id}`, payload);
      }

      if (onSave) {
        onSave();
      }
      onClose();
    } catch (err) {
      console.error('Error updating transaction:', err);
      setError(err.response?.data?.detail || 'Failed to update transaction');
    } finally {
      setLoading(false);
    }
  };

  const currentSubcategories = expenseData.category ? (categories[expenseData.category]?.subcategories || []) : [];
  const symbol = getCurrencySymbol(isIncome ? incomeData.currency : expenseData.currency);

  // Filter wallets - exclude household wallets for personal expenses
  const availableWallets = isHouseholdExpense 
    ? wallets 
    : wallets.filter(w => w.wallet_type !== 'household');

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content edit-modal" onClick={(e) => e.stopPropagation()}>
        <h2>{isIncome ? '✏️ Edit Income' : isHouseholdExpense ? '✏️ Edit Household Expense' : '✏️ Edit Expense'}</h2>
        
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* INCOME FORM */}
          {isIncome && (
            <>
              <div className="form-group">
                <label>Pot *</label>
                <select 
                  value={incomeData.wallet_id} 
                  onChange={(e) => handleWalletChange(e.target.value, true)} 
                  required
                >
                  <option value="">Select pot...</option>
                  {availableWallets.map(wallet => (
                    <option key={wallet.id} value={wallet.id}>
                      {wallet.name} ({wallet.currency})
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Date *</label>
                <input
                  type="date"
                  value={incomeData.date}
                  onChange={(e) => handleIncomeChange('date', e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label>Source *</label>
                <input
                  type="text"
                  value={incomeData.source}
                  onChange={(e) => handleIncomeChange('source', e.target.value)}
                  placeholder="e.g., Salary, Freelance, etc."
                  required
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <input
                  type="text"
                  value={incomeData.description}
                  onChange={(e) => handleIncomeChange('description', e.target.value)}
                  placeholder="e.g., January salary"
                />
              </div>

              <div className="form-section">
                <h3>💰 Amount Breakdown</h3>
                
                <div className="form-group">
                  <label>Gross Amount ({symbol}) *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={incomeData.gross_amount}
                    onChange={(e) => handleIncomeChange('gross_amount', e.target.value)}
                    required
                  />
                </div>

                <div className="form-row deductions">
                  <div className="form-group">
                    <label>Income Tax ({symbol})</label>
                    <input
                      type="number"
                      step="0.01"
                      value={incomeData.income_tax}
                      onChange={(e) => handleIncomeChange('income_tax', e.target.value)}
                    />
                  </div>

                  <div className="form-group">
                    <label>Social Contributions ({symbol})</label>
                    <input
                      type="number"
                      step="0.01"
                      value={incomeData.social_contributions}
                      onChange={(e) => handleIncomeChange('social_contributions', e.target.value)}
                    />
                  </div>

                  <div className="form-group">
                    <label>Student Loan ({symbol})</label>
                    <input
                      type="number"
                      step="0.01"
                      value={incomeData.student_loan}
                      onChange={(e) => handleIncomeChange('student_loan', e.target.value)}
                    />
                  </div>
                </div>

                <div className="net-amount-display">
                  <span>Net Amount:</span>
                  <span className="net-value">{symbol}{calculateNetAmount().toFixed(2)}</span>
                </div>
              </div>

              <div className="form-group">
                <label>Notes</label>
                <textarea
                  value={incomeData.notes}
                  onChange={(e) => handleIncomeChange('notes', e.target.value)}
                  rows="2"
                  placeholder="Optional notes..."
                />
              </div>
            </>
          )}

          {/* EXPENSE FORM (Personal & Household) */}
          {!isIncome && (
            <>
              {!isHouseholdExpense && (
                <div className="form-group">
                  <label>Pot *</label>
                  <select 
                    value={expenseData.wallet_id} 
                    onChange={(e) => handleWalletChange(e.target.value, false)} 
                    required
                  >
                    <option value="">Select pot...</option>
                    {availableWallets.map(wallet => (
                      <option key={wallet.id} value={wallet.id}>
                        {wallet.name} ({wallet.currency})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {isHouseholdExpense && (
                <div className="form-group">
                  <label>Household</label>
                  <input
                    type="text"
                    value={transaction.household_name || 'Household'}
                    disabled
                    className="disabled-input"
                  />
                  <small className="form-hint">Household cannot be changed</small>
                </div>
              )}

              <div className="form-group">
                <label>Date *</label>
                <input
                  type="date"
                  value={expenseData.date}
                  onChange={(e) => handleExpenseChange('date', e.target.value)}
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Category *</label>
                  <select 
                    value={expenseData.category} 
                    onChange={(e) => handleCategoryChange(e.target.value)} 
                    required 
                    disabled={categoriesLoading}
                  >
                    <option value="">Select category...</option>
                    {Object.keys(categories).sort().map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Subcategory</label>
                  <select 
                    value={expenseData.subcategory} 
                    onChange={(e) => handleExpenseChange('subcategory', e.target.value)}
                    disabled={!expenseData.category}
                  >
                    <option value="">Select subcategory...</option>
                    {currentSubcategories.map(sub => (
                      <option key={sub} value={sub}>{sub}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Description</label>
                <input
                  type="text"
                  value={expenseData.description}
                  onChange={(e) => handleExpenseChange('description', e.target.value)}
                  placeholder="e.g., Weekly groceries"
                />
              </div>

              <div className="form-group">
                <label>Amount ({symbol}) *</label>
                <input
                  type="number"
                  step="0.01"
                  value={expenseData.amount}
                  onChange={(e) => handleExpenseChange('amount', e.target.value)}
                  required
                />
                {isHouseholdExpense && (
                  <small className="form-hint">
                    Note: Changing the amount will affect all household members' splits proportionally.
                  </small>
                )}
              </div>

              <div className="form-group">
                <label>Notes</label>
                <textarea
                  value={expenseData.notes}
                  onChange={(e) => handleExpenseChange('notes', e.target.value)}
                  rows="2"
                  placeholder="Optional notes..."
                />
              </div>
            </>
          )}

          <div className="modal-actions">
            <button type="button" onClick={onClose} disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="primary" disabled={loading}>
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EditTransactionModal;
